import {Observable, of as observableOf} from 'rxjs';

import {catchError, first, map, takeWhile} from 'rxjs/operators';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgZone} from '@angular/core';
import {AbstractControlComponent} from '../abstract-control';
import {ViewTabComponent} from '../../viewtab.component';
import {ErrorService} from '../../../../error/error.service';
import {optioncontrolRequired} from '../validators/optioncontrol-required-validator';
import {Activity, ActivityType} from './activity';
import * as convert from 'xml-js';
import {AddDelayActivity} from './activities/add-delay';
import {CreateResourceActivity} from './activities/create-resource';
import {DeleteResourcesActivity} from './activities/delete-resources';
import {GenerateUniqueValueActivity} from './activities/generate-unique-value';
import {RequestApprovalActivity} from './activities/request-approval';
import {RunPowerShellScriptActivity} from './activities/run-power-shell-script';
import {SendEmailNotificationActivity} from './activities/send-email-notification';
import {UpdateResourcesActivity} from './activities/update-resources';
import {VerifyRequestActivity} from './activities/verify-request';
import {UnknownActivity} from './activities/unknown';
import {LanguageService} from '../../../../language/language.service';
import {CacheService} from '../../../../cache/cache.service';
import {ResourceWithPermissionHints} from '../../../resource';
import {EnvironmentConfigService} from '../../../../core/services/environment-config.service';
import {ResourceService} from '../../../resource.service';
import {IDGeneratorActivity} from './activities/id-generator';
import {LazyDialogService} from './activities/lazy-dialog.service';
import {CustomActivity} from './activities/custom';

@Component({
  selector: 'ctrl-processdesigner',
  templateUrl: 'ctrl-processdesigner.component.html',
  styleUrls: ['./ctrl-processdesigner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CtrlProcessDesignerComponent extends AbstractControlComponent {
  constructor(
    _parent: ViewTabComponent,
    changeDetector: ChangeDetectorRef,
    private cacheService: CacheService,
    _ngZone: NgZone,
    errorService: ErrorService,
    private lazyDialogService: LazyDialogService,
    private envConfigService: EnvironmentConfigService,
  ) {
    super(_parent, 'XOML', 'XOML', changeDetector, _ngZone, errorService);
  }

  @Input() resource: ResourceWithPermissionHints;

  activities: Activity[] = [];
  originalNamespaces: any = {};

  private locked: boolean = false; // prohibit array change during edit

  protected initValidators(): void {
    if (this._config == null || this.formControl == null) {
      throw Error('Initialized target too early.');
    }

    this._config
      .getBooleanPropertyValues('Required')
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        takeWhile(() => this.alive),
      )
      .subscribe(required => {
        if (required === true) {
          this.formControl.setValidators([optioncontrolRequired()]);
        } else {
          this.formControl.clearValidators();
        }
        this.formControl.updateValueAndValidity({emitEvent: false});
      });
  }

  private latestXomlHash = null;

  protected formatSource(source: any): any {
    try {
      this.parseXOML(source);
    } catch (e) {
      this.handleError(e);
    }
    return source;
  }

  private parseXOML(xoml: string): void {
    if (xoml === undefined || xoml == null || typeof xoml !== 'string' || xoml.length === 0) return;

    let hash = 0;
    for (let i = 0; i < xoml.length; i++) {
      const char = xoml.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }

    if (this.latestXomlHash === hash) {
      // nothing changed
      return;
    }

    this.latestXomlHash = hash;

    const obj = convert.xml2js(xoml, {
      attributesKey: '$',
    });

    if (obj == null) {
      throw 'Failed converting XOML to JS';
    }

    if (obj.elements == undefined || !Array.isArray(obj.elements) || obj.elements.length !== 1) {
      throw 'Failed parsing XOML';
    }

    const root = obj.elements[0];
    if (stripXMLPrefix(root.$['x:Name']) !== 'SequentialWorkflow') {
      throw 'Cannot parse other workflows than SequentialWorkflow';
    }

    this.originalNamespaces = {};
    for (let xmlKey in root.$) {
      if (xmlKey !== 'xmlns:x' && xmlKey.startsWith('xmlns:') && !xmlKey.endsWith('ID')) {
        this.originalNamespaces[xmlKey] = root.$[xmlKey];
      }
    }

    this.activities = [];

    for (let activityNode of root.elements) {
      const name = stripXMLPrefix(activityNode.name);
      let activity: Activity;
      switch (name) {
        case 'AddDelay':
          activity = new AddDelayActivity(activityNode, this.lazyDialogService);
          break;
        case 'CreateResource':
          activity = new CreateResourceActivity(activityNode, this.lazyDialogService);
          break;
        case 'DeleteResources':
          activity = new DeleteResourcesActivity(activityNode, this.lazyDialogService);
          break;
        case 'GenerateUniqueValue':
          activity = new GenerateUniqueValueActivity(activityNode, this.lazyDialogService);
          break;
        case 'RequestApproval':
          activity = new RequestApprovalActivity(activityNode, this.lazyDialogService);
          break;
        case 'RunPowerShellScript':
          activity = new RunPowerShellScriptActivity(activityNode, this.lazyDialogService);
          break;
        case 'SendEmailNotification':
          activity = new SendEmailNotificationActivity(activityNode, this.lazyDialogService);
          break;
        case 'UpdateResources':
          activity = new UpdateResourcesActivity(activityNode, this.lazyDialogService);
          break;
        case 'VerifyRequest':
          activity = new VerifyRequestActivity(activityNode, this.lazyDialogService);
          break;
        case 'IDGenerator':
          activity = new IDGeneratorActivity(activityNode, this.lazyDialogService);
          break;
        default:
          activity = new UnknownActivity(activityNode, this.lazyDialogService);
          break;
      }

      if (activity != null) {
        this.activities.push(activity);
      }
    }
  }

  public saveXOML(): void {
    const xoml = this.generateXOML();
    this.formControl.setValue(xoml, {emitEvent: true});
    this.changeDetector.markForCheck();
  }

  public moveDown(index: number): void {
    if (this.locked) return;
    if (index >= this.activities.length - 1 || index < 0) {
      return;
    }

    const obj = this.activities[index];
    this.activities[index] = this.activities[index + 1];
    this.activities[index + 1] = obj;
    this.saveXOML();
  }

  public moveUp(index: number): void {
    if (this.locked) return;
    if (index >= this.activities.length || index < 1) {
      return;
    }

    const obj = this.activities[index];
    this.activities[index] = this.activities[index - 1];
    this.activities[index - 1] = obj;
    this.saveXOML();
  }

  public delete(index: number): void {
    if (this.locked) return;
    this.activities.splice(index, 1);
    this.saveXOML();
  }

  public edit(index: number): void {
    this.locked = true;

    const activity = this.activities[index];

    const closed = activity.openDialog();
    if (closed != null) {
      closed.pipe(first()).subscribe(dlgResult => {
        if (dlgResult === true) {
          this.saveXOML();
        }
      });
    }

    this.locked = false;
  }

  activityTypeAllowed(type: ActivityType): boolean {
    const requestPhaseAttrName = this.envConfigService.getAttributeName('RequestPhase');
    if (
      this.resource == null ||
      requestPhaseAttrName == null ||
      this.resource[requestPhaseAttrName] == null
    )
      return false;
    const requestPhase = this.resource[requestPhaseAttrName].Values;
    if (requestPhase === 'Action') {
      return (
        [
          ActivityType.AddDelay,
          ActivityType.CreateResource,
          ActivityType.DeleteResources,
          ActivityType.GenerateUniqueValue,
          ActivityType.RunPowerShellScript,
          ActivityType.SendEmailNotification,
          ActivityType.UpdateResources,
          ActivityType.IDGenerator,
        ].indexOf(type) !== -1
      );
    } else if (requestPhase === 'Authorization') {
      return (
        [
          ActivityType.AddDelay,
          ActivityType.RequestApproval,
          ActivityType.RunPowerShellScript,
          ActivityType.SendEmailNotification,
          ActivityType.UpdateResources,
          ActivityType.VerifyRequest,
          ActivityType.IDGenerator,
        ].indexOf(type) !== -1
      );
    } else {
      return false;
    }
  }

  public add(type: ActivityType): void {
    let activity: Activity = null;
    switch (type) {
      case ActivityType.AddDelay:
        activity = new AddDelayActivity(null, this.lazyDialogService);
        break;
      case ActivityType.CreateResource:
        activity = new CreateResourceActivity(null, this.lazyDialogService);
        break;
      case ActivityType.DeleteResources:
        activity = new DeleteResourcesActivity(null, this.lazyDialogService);
        break;
      case ActivityType.GenerateUniqueValue:
        activity = new GenerateUniqueValueActivity(null, this.lazyDialogService);
        break;
      case ActivityType.RequestApproval:
        activity = new RequestApprovalActivity(null, this.lazyDialogService);
        break;
      case ActivityType.RunPowerShellScript:
        activity = new RunPowerShellScriptActivity(null, this.lazyDialogService);
        break;
      case ActivityType.SendEmailNotification:
        activity = new SendEmailNotificationActivity(null, this.lazyDialogService);
        break;
      case ActivityType.UpdateResources:
        activity = new UpdateResourcesActivity(null, this.lazyDialogService);
        break;
      case ActivityType.VerifyRequest:
        activity = new VerifyRequestActivity(null, this.lazyDialogService);
        break;
      case ActivityType.IDGenerator:
        activity = new IDGeneratorActivity(null, this.lazyDialogService);
        break;
      case ActivityType.Custom:
        activity = new CustomActivity(this.lazyDialogService);
        break;
    }
    if (activity != null) {
      const closed = activity.openDialog();
      if (closed != null) {
        closed.pipe(first()).subscribe(dlgResult => {
          if (dlgResult === true) {
            this.activities.push(activity);
            this.saveXOML();
          }
        });
      }
    }
    this.changeDetector.detectChanges();
  }

  public generateXOML(): string {
    const obj = {
      elements: [
        {
          type: 'element',
          name: 'ns0ID:SequentialWorkflow',
          $: {
            ActorId: '00000000-0000-0000-0000-000000000000',
            RequestId: '00000000-0000-0000-0000-000000000000',
            'x:Name': 'SequentialWorkflow',
            TargetId: '00000000-0000-0000-0000-000000000000',
            WorkflowDefinitionId: '00000000-0000-0000-0000-000000000000',
            xmlns: 'http://schemas.microsoft.com/winfx/2006/xaml/workflow',
            'xmlns:x': 'http://schemas.microsoft.com/winfx/2006/xaml',
            'xmlns:ns0ID':
              'clr-namespace:Microsoft.ResourceManagement.Workflow.Activities;Assembly=Microsoft.ResourceManagement, Version=4.5.0.0, Culture=neutral, PublicKeyToken=31bf3856ad364e35',
            'xmlns:ns1ID':
              'clr-namespace:MicrosoftServices.IdentityManagement.WorkflowActivityLibrary.Activities;Assembly=MicrosoftServices.IdentityManagement.WorkflowActivityLibrary, Version=2.19.112.0, Culture=neutral, PublicKeyToken=f10e8ca489c1d864',
            'xmlns:ns2ID':
              'clr-namespace:MicrosoftServices.IdentityManagement.WorkflowActivityLibrary.ComponentActivities;Assembly=MicrosoftServices.IdentityManagement.WorkflowActivityLibrary, Version=2.19.112.0, Culture=neutral, PublicKeyToken=f10e8ca489c1d864',
            'xmlns:ns3ID':
              'clr-namespace:identityplusidgenerator;Assembly=identityplusidgenerator, Version=1.0.0.1, Culture=neutral, PublicKeyToken=1961729d2501fa23',
            'xmlns:ns4ID':
              'clr-namespace:System;Assembly=mscorlib, Version=4.0.0.0, Culture=neutral, PublicKeyToken=b77a5c561934e089',
          },
          elements: [],
        },
      ],
    };

    for (let [key, value] of Object.entries(this.originalNamespaces)) {
      obj.elements[0].$[key] = value;
    }

    if (this.activities != null && Array.isArray(this.activities) && this.activities.length > 0) {
      let indicesToDelete = [];
      this.activities.forEach((a: Activity, index: number) => {
        let activityObj = a.objForXML();
        if (activityObj == null || (Array.isArray(activityObj) && activityObj.length === 0)) {
          indicesToDelete.push(index);
        } else if (Array.isArray(activityObj)) {
          obj.elements[0].elements.push(...activityObj);
        } else {
          obj.elements[0].elements.push(activityObj);
        }
      });
      indicesToDelete
        .sort()
        .reverse()
        .forEach(i => this.activities.splice(i, 1));
    } else {
      return null;
    }

    const fnHtmlEntitiesEncode = (value: string) => {
      return value
        .replace(/&quot;/g, '"') // gets replaced by library
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;')
        .replace(new RegExp('\r?\n', 'g'), '&#xA;');
    };

    const xml = convert.js2xml(obj, {
      attributesKey: '$',
      attributeValueFn: fnHtmlEntitiesEncode,
    });

    return xml;
  }

  ignoreCatchObservable(obs: Observable<any>): Observable<any> {
    if (obs == null) return null;
    return obs.pipe(
      catchError(error => {
        return observableOf(null);
      }),
    );
  }

  translationObs(name: string, defaultValue: string): Observable<string> {
    const obs = this.cacheService
      .getLanguageResource()
      .pipe(map(lr => LanguageService.translate(name, defaultValue, lr)));
    return this.ignoreCatchObservable(obs);
  }

  camelCaseToNormal(str: string) {
    return ResourceService.convertCamelCaseToNormal(str).replace('Power Shell', 'PowerShell');
  }

  protected readonly ActivityType = ActivityType;
}

export function stripXMLPrefix(str: string) {
  const prefixMatch = new RegExp(/(?!xmlns)^.*:/);
  return str.replace(prefixMatch, '');
}
